import React, { FormEvent } from "react";
import { ChangeEvent, Fragment, MouseEvent } from "react";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import TabControl from "../TabControl";
import ValidateInput from "../ValidateInput";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import { WellKnownGroups } from "../AuthContext";
import { formatUSD } from "../../Utilities";

import "../../blocks.css";
import "../../schedule.css";
import "../viewer.css";

interface IBonusAdminViewState {
    changedFields: string[];
    hasChanged: boolean;
    isLoading: boolean | string;

    bonuses: { [groupId: string]: { [type: string]: any[] } };
    brackets: any[];

    bracketNewAmount: string;
    bracketNewValue: string;
    isEditingBrackets: boolean;
    isSavingBrackets: boolean;

    isSavingSurgical: boolean | string;
    hasSavedSurgical: boolean;

    surgicalStartingPercent: string;
    surgicalIncrementPercent: string;
    surgicalDollarValue: string;

    surgicalRescheduleMode: string;
    surgicalRescheduleInterval: string;
    surgicalRescheduleValue: string;

    surgicalCancelRange: string;
    surgicalCancelRangeSchedule: string;
    surgicalCancelValue: string;

    marketingManagementBrackets: any[];

    lastTabSelected: number;

}

class BonusAdminView extends React.Component<RouteComponentPropsHistory<any, any>, IBonusAdminViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        this.state = {
            changedFields: [],
            hasChanged: false,
            isLoading: true,

            bonuses: {},
            brackets: [],

            bracketNewAmount: "",
            bracketNewValue: "",
            isEditingBrackets: false,
            isSavingBrackets: false,

            isSavingSurgical: false,
            hasSavedSurgical: false,

            surgicalStartingPercent: "0",
            surgicalIncrementPercent: "0",
            surgicalDollarValue: "0.00",

            surgicalRescheduleMode: "0",
            surgicalRescheduleInterval: "0",
            surgicalRescheduleValue: "0.00",

            surgicalCancelRange: "",
            surgicalCancelRangeSchedule: "",
            surgicalCancelValue: "",

            marketingManagementBrackets: [],

            lastTabSelected: (props.location.state?.lastTabSelected) || 0
        };

        this.toggleBracketsEdit = this.toggleBracketsEdit.bind(this);
        this.addNewBracket = this.addNewBracket.bind(this);
        this.deleteBracket = this.deleteBracket.bind(this);
        this.addNewMarketingManagementBracket = this.addNewMarketingManagementBracket.bind(this);
        this.deleteMarketingManagementBracket = this.deleteMarketingManagementBracket.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.saveSurgical = this.saveSurgical.bind(this);
        this.toggleMarketingManagementBracketsEdit = this.toggleMarketingManagementBracketsEdit.bind(this);
        this.handleTabChanged = this.handleTabChanged.bind(this);
    }

    public async componentDidMount() {
        try {
            const groups = [
                WellKnownGroups.HfTeam,
                WellKnownGroups.PatientCareRep,
                WellKnownGroups.SurgicalConsultant,
                WellKnownGroups.Provider,
            ];

            const [bonuses, brackets, surgical, marketingManagementBrackets] = await Promise.all([
                V2.get<any[]>(`bonus?${groups.map(a => "groupid=" + a).join("&")}`),
                V2.get<any[]>("bonus/brackets"),
                V2.get<any>("bonus/surgical"),
                V2.get<any[]>("bonus/brackets/marketing")
            ]);

            const bonusGroups: { [groupId: string]: { [type: string]: any[] } } = {};

            for (const item of bonuses) {
                if (!bonusGroups[item.groupId]) {
                    bonusGroups[item.groupId] = {};
                }
                if (!bonusGroups[item.groupId][item.type]) {
                    bonusGroups[item.groupId][item.type] = [];
                }

                bonusGroups[item.groupId][item.type].push(item);
            }

            this.setState({
                isLoading: false,

                bonuses: bonusGroups,
                brackets,

                surgicalStartingPercent: surgical.startingPercent,
                surgicalIncrementPercent: surgical.incrementPercent,
                surgicalDollarValue: surgical.percentValue,

                surgicalRescheduleMode: surgical.rescheduleMode,
                surgicalRescheduleInterval: surgical.rescheduleInterval,
                surgicalRescheduleValue: surgical.rescheduleValue,

                surgicalCancelRange: surgical.cancelRangeDays,
                surgicalCancelRangeSchedule: surgical.cancelRangeScheduleDays,
                surgicalCancelValue: surgical.cancelValue,

                marketingManagementBrackets
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="error-box">{this.state.isLoading}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <TabControl headers={["HF Team", "Patient Care Reps", "Surgical Consultant", "Providers", "Surgical Outcomes", "Marketing Manager"]} defaultSelectedIndex={this.state.lastTabSelected} onTabChanged={this.handleTabChanged}>
                    {this.renderBasicBonusTab(WellKnownGroups.HfTeam)}
                    {this.renderBasicBonusTab(WellKnownGroups.PatientCareRep)}
                    {this.renderBasicBonusTab(WellKnownGroups.SurgicalConsultant)}
                    <div>
                        <h3>NP Bonuses</h3>
                        <table className="section-table table-vertical wrap">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className="col-smallest">Unit</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Bonus Types</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    !this.state.bonuses[WellKnownGroups.Provider]
                                        ? <tr><td colSpan={10}>No bonus types found.</td></tr>
                                        : this.state.bonuses[WellKnownGroups.Provider][2].map(a => (
                                            <tr key={a.id}>
                                                <td>{a.name}</td>
                                                <td>{this.unitToString(a.unit)}</td>
                                                <td><Link to={`/bonus/admin/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                        &nbsp;
                        <div className="action-bar-dialog">
                            <Link to={{ pathname: "/bonus/admin/create", state: { groupId: WellKnownGroups.Provider, type: 2 } }} className="button btn-plus push-right">Add Bonus Type</Link>
                        </div>
                    </div>
                    <div>
                        <h3>Surgical Outcomes</h3>
                        <form className="input-form" onSubmit={this.saveSurgical}>
                            <div className="column-root">
                                <section className="info-block">
                                    <header>
                                        <h3>Basic Information</h3>
                                    </header>

                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-starting-percent" className="required">Starting %</label>
                                            <ValidateInput type="number" id="frm-starting-percent" name="surgicalStartingPercent" value={this.state.surgicalStartingPercent} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-increment-percent" className="required">Increment %</label>
                                            <ValidateInput type="number" id="frm-increment-percent" name="surgicalIncrementPercent" value={this.state.surgicalIncrementPercent} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-base-value" className="required">Dollar Value</label>
                                            <ValidateInput type="number" id="frm-base-value" name="surgicalDollarValue" value={this.state.surgicalDollarValue} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                    </section>
                                    <section>
                                        <em>The bonus recipient is specified by adding the employee to the <b>Surgical Outcomes</b> group.</em>
                                    </section>
                                </section>
                                <section className="info-block">
                                    <header>
                                        <h3>Penalties</h3>
                                    </header>

                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-reschedule-mode" className="required">Reschedule mode</label>
                                            <ValidateInput type="select" id="frm-reschedule-mode" name="surgicalRescheduleMode" value={this.state.surgicalRescheduleMode} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                                <option value="0">Every (n)th time</option>
                                                <option value="1">(N)th time onward</option>
                                            </ValidateInput>
                                        </div>
                                    </section>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-reschedule-interval" className="required">Reschedule count</label>
                                            <ValidateInput type="number" id="frm-reschedule-interval" name="surgicalRescheduleInterval" value={this.state.surgicalRescheduleInterval} step={1} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-reschedule-value" className="required">Deduction</label>
                                            <ValidateInput type="number" id="frm-reschedule-value" name="surgicalRescheduleValue" value={this.state.surgicalRescheduleValue} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                    </section>

                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-cancel-within" className="required">Canceled within <small>(days)</small></label>
                                            <ValidateInput type="number" id="frm-cancel-within" name="surgicalCancelRange" value={this.state.surgicalCancelRange} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                        <div className="input-form-group" />
                                    </section>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-cancel-more-than" className="required">Creation range <small>(days)</small></label>
                                            <ValidateInput type="number" id="frm-cancel-more-than" name="surgicalCancelRangeSchedule" value={this.state.surgicalCancelRangeSchedule} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-cancel-value" className="required">Deduction</label>
                                            <ValidateInput type="number" id="frm-cancel-value" name="surgicalCancelValue" value={this.state.surgicalCancelValue} step={0.01} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                        </div>
                                    </section>
                                </section>
                            </div>
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button className="button push-right text-with-icon" disabled={this.state.isSavingSurgical === true || this.state.hasSavedSurgical}>{this.state.hasSavedSurgical ? "Saved!" : "Save"}</button>
                            </div>
                        </form>
                    </div>
                    <div>
                        <h3>Completed Case Brackets</h3>
                        <table className={"section-table table-vertical wrap" + (this.state.isEditingBrackets ? " table-edit" : "")}>
                            <thead>
                                <tr>
                                    <th>Cases</th>
                                    <th className="col-smallest">Dollars</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Completed Case Brackets</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.isEditingBrackets
                                        ? <Fragment>
                                            {this.state.marketingManagementBrackets.map((a, i) => (
                                                <tr key={a.id}>
                                                    <td data-caption="Cases: "><input type="number" name={`marketingManagementBrackets:${i}:cases`} value={a.cases} onChange={this.formInputChanged} /></td>
                                                    <td data-caption="Dollars: "><input type="number" name={`marketingManagementBrackets:${i}:value`} value={a.value} onChange={this.formInputChanged} /></td>
                                                    <td><button type="button" className="button red small icon-times" onClick={() => this.deleteMarketingManagementBracket(i)} /></td>
                                                </tr>))
                                            }
                                            <tr>
                                                <td data-caption="Cases: "><input type="number" name="bracketNewAmount" value={this.state.bracketNewAmount} onChange={this.formInputChanged} placeholder="Cases" /></td>
                                                <td data-caption="Dollars: "><input type="number" name="bracketNewValue" value={this.state.bracketNewValue} onChange={this.formInputChanged} placeholder="Dollars" /></td>
                                                <td><button type="button" className="button small icon-plus" onClick={this.addNewMarketingManagementBracket} /></td>
                                            </tr>
                                        </Fragment>
                                        : (!this.state.marketingManagementBrackets || this.state.marketingManagementBrackets.length === 0
                                            ? <tr><td colSpan={10}>No brackets.</td></tr>
                                            : this.state.marketingManagementBrackets.map(a => (
                                                <tr key={a.id}>
                                                    <td>{a.cases}</td>
                                                    <td className="col-right">{formatUSD(a.value)}</td>
                                                    <td />
                                                </tr>
                                            )))
                                }
                            </tbody>
                        </table>
                        &nbsp;
                        <div className="action-bar-dialog">
                            <button className="button push-right text-with-icon" onClick={this.toggleMarketingManagementBracketsEdit} disabled={this.state.isSavingBrackets}>
                                {
                                    this.state.isSavingBrackets
                                        ? <span>Saving... <span className="spinner lds-dual-ring-small" /></span>
                                        : this.state.isEditingBrackets
                                            ? <Fragment><i className="icon-check" /><span>Save Brackets</span></Fragment>
                                            : <Fragment><i className="icon-pencil" /><span>Edit Brackets</span></Fragment>
                                }
                            </button>
                        </div>
                        &nbsp;
                        <section>
                            <em>The bonus recipient is specified by adding the employee to the <b>Marketing Manager</b> group.</em>
                        </section>
                    </div>
                </TabControl>
            </div>
        );
    }

    private unitToString(unit: number) {
        switch (unit) {
            case 0: return "Points";
            case 1: return "Currency";
            default: return `Unknown (${unit})`;
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: (state: Partial<IBonusAdminViewState>) => void) {
        let name = e.currentTarget.name;
        let value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            const split = name.match(/(.+?):(.+)/);
            if (split) {
                // Array indexer
                name = split[1];
                const newValue = value;
                value = (s as any)[name];

                const split2 = split[2].match(/(.+?):(.+)/);
                if (split2) {
                    value[split2[1]][split2[2]] = newValue;
                }
                else {
                    value[split[2]] = newValue;
                }
            }

            if (!s.changedFields.some(a => a === name)) {
                s.changedFields.push(name);
            }

            const newState = {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            } as any;

            if (callback) {
                callback(newState);
            }

            return newState;
        });
    }

    private async toggleBracketsEdit() {
        if (this.state.isEditingBrackets) {
            // Save brackets
            this.setState({
                isSavingBrackets: true
            });

            await V2.put(`bonus/brackets`, this.state.brackets);

            this.setState({
                hasChanged: false,
                isEditingBrackets: false,
                isSavingBrackets: false,
            });
        }
        else {
            // Show edit inputs
            this.setState({
                isEditingBrackets: true
            });
        }
    }

    private async toggleMarketingManagementBracketsEdit() {
        if (this.state.isEditingBrackets) {
            // Save brackets
            this.setState({
                isSavingBrackets: true
            });

            await V2.put(`bonus/brackets/marketing`, this.state.marketingManagementBrackets);

            this.setState({
                hasChanged: false,
                isEditingBrackets: false,
                isSavingBrackets: false,
            });
        }
        else {
            // Show edit inputs
            this.setState({
                isEditingBrackets: true
            });
        }
    }

    private addNewMarketingManagementBracket() {
        if (!this.state.bracketNewAmount || !this.state.bracketNewValue) {
            return;
        }

        this.setState(s => {
            s.marketingManagementBrackets.push({
                cases: (+s.bracketNewAmount).toFixed(0),
                id: Date.now().toString(),
                value: (+s.bracketNewValue).toFixed(2)
            });

            return {
                bracketNewAmount: "",
                bracketNewValue: "",
                marketingManagementBrackets: s.marketingManagementBrackets.sort((a, b) => a.cases - b.cases),
            };
        });
    }

    private deleteMarketingManagementBracket(index: number) {
        if (!index && index !== 0) {
            return;
        }

        this.setState(s => {
            s.marketingManagementBrackets.splice(index, 1);

            return {
                marketingManagementBrackets: s.marketingManagementBrackets,
            };
        });
    }


    private addNewBracket() {
        if (!this.state.bracketNewAmount || !this.state.bracketNewValue) {
            return;
        }

        this.setState(s => {
            s.brackets.push({
                amount: (+s.bracketNewAmount).toFixed(2),
                id: Date.now().toString(),
                value: (+s.bracketNewValue).toFixed(2)
            });

            return {
                bracketNewAmount: "",
                bracketNewValue: "",
                brackets: s.brackets.sort((a, b) => a.amount - b.amount),
            };
        });
    }

    private deleteBracket(e: MouseEvent<HTMLButtonElement>) {
        /* tslint:disable-next-line:no-string-literal */
        const indexData = e.currentTarget.dataset["itemIndex"];
        const index = indexData && +indexData;
        if (!index && index !== 0) {
            return;
        }

        this.setState(s => {
            s.brackets.splice(index, 1);

            return {
                brackets: s.brackets,
            };
        });
    }

    private async saveSurgical(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isSavingSurgical: true,
                hasSavedSurgical: false,
            });

            const data = {
                startingPercent: this.state.surgicalStartingPercent,
                incrementPercent: this.state.surgicalIncrementPercent,
                percentValue: this.state.surgicalDollarValue,

                rescheduleMode: this.state.surgicalRescheduleMode,
                rescheduleInterval: this.state.surgicalRescheduleInterval,
                rescheduleValue: this.state.surgicalRescheduleValue,

                cancelRangeDays: this.state.surgicalCancelRange,
                cancelRangeScheduleDays: this.state.surgicalCancelRangeSchedule,
                cancelValue: this.state.surgicalCancelValue
            };

            await V2.put("bonus/surgical", data);

            this.setState({
                isSavingSurgical: false,
                hasSavedSurgical: true,
            }, () => {
                window.setTimeout(() => { this.setState({ hasSavedSurgical: false }) }, 3000);
            });
        }
        catch (e) {
            this.setState({
                isSavingSurgical: e.message || e || "Could not save data"
            });
        }
    };

    private renderBasicBonusTab(groupId: string) {
        return <div>
            <div className="column-root">
                <section>
                    <h3>Office appointment bonuses</h3>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th className="col-smallest">Unit</th>
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th>Bonus Types</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                !this.state.bonuses[groupId]?.[1]?.length
                                    ? <tr><td colSpan={10}>No bonus types found.</td></tr>
                                    : this.state.bonuses[groupId][1].map(a => (
                                        <tr key={a.id}>
                                            <td>{a.name}</td>
                                            <td>{this.unitToString(a.unit)}</td>
                                            <td><Link to={`/bonus/admin/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                        </tr>
                                    ))
                            }
                        </tbody>
                    </table>
                    &nbsp;
                    <div className="action-bar-dialog">
                        <Link to={{ pathname: "/bonus/admin/create", state: { groupId: groupId, type: 1 } }} className="button btn-plus push-right">Add Appointment Bonus</Link>
                    </div>
                </section>
                <section>
                    <h3>Survey bonus</h3>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th className="col-smallest">Unit</th>
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th>Bonus Types</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                !this.state.bonuses[groupId]?.[0]?.length
                                    ? <tr><td colSpan={10}>No bonus types found.</td></tr>
                                    : this.state.bonuses[groupId][0].map(a => (
                                        <tr key={a.id}>
                                            <td>{a.name}</td>
                                            <td>{this.unitToString(a.unit)}</td>
                                            <td><Link to={`/bonus/admin/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                        </tr>
                                    ))
                            }
                        </tbody>
                    </table>
                    &nbsp;
                    {!this.state.bonuses[groupId]?.[0]?.length
                        ? <div className="action-bar-dialog">
                            <Link to={{ pathname: "/bonus/admin/create", state: { groupId: groupId, type: 0 } }} className="button btn-plus push-right">Add Survey Bonus</Link>
                        </div>
                        : null}
                </section>
            </div>
            <div className="column-root">
                <section>
                    <h3>Surgery bonus</h3>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th className="col-smallest">Unit</th>
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th>Bonus Types</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                !this.state.bonuses[groupId]?.[2]?.length
                                    ? <tr><td colSpan={10}>No bonus types found.</td></tr>
                                    : this.state.bonuses[groupId][2].map(a => (
                                        <tr key={a.id}>
                                            <td>{a.name}</td>
                                            <td>{this.unitToString(a.unit)}</td>
                                            <td><Link to={`/bonus/admin/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                        </tr>
                                    ))
                            }
                        </tbody>
                    </table>
                    &nbsp;
                    {!this.state.bonuses[groupId]?.[2]?.length
                        ? <div className="action-bar-dialog">
                            <Link to={{ pathname: "/bonus/admin/create", state: { groupId: groupId, type: 2 } }} className="button btn-plus push-right">Add Surgery Bonus</Link>
                        </div>
                        : null}
                </section>
                <section className="section-spacer" />
            </div>
        </div>;
    }

    private handleTabChanged(index: number) {
        const location = this.props.location;
        if (!location.state) {
            location.state = {};
        }

        location.state.lastTabSelected = index;

        this.props.history.replace(location);
    }
}

export default withTitle("Bonus Management", BonusAdminView);
